import { useState } from 'react'
import Modal from './Modal'
import { useCostSummary, useSparks, useAddSparks } from '../../api/hooks'
import type { NodeUsageItem } from '../../api/client'

interface UsageCostModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string | undefined
}

const NODE_TYPE_LABELS: Record<string, string> = {
  director: 'Director',
  character_designer: 'Character Designer',
  location_designer: 'Location Designer',
  object_designer: 'Object Designer',
  storyboard_designer: 'Storyboard Designer',
  image_generator: 'Image Generator',
  video_generator: 'Video Generator',
  script_writer: 'Script Writer',
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remaining = Math.round(seconds % 60)
  return `${minutes}m ${remaining}s`
}

function formatTokens(count: number): string {
  if (count === 0) return '0'
  if (count < 1000) return String(count)
  return `${(count / 1000).toFixed(1)}K`
}

function AddSparksSection() {
  const { data: sparksData, isLoading: sparksLoading } = useSparks()
  const addSparksMutation = useAddSparks()
  const [amount, setAmount] = useState('')

  const handleAddSparks = async () => {
    const sparks = parseInt(amount, 10)
    if (isNaN(sparks) || sparks <= 0) return

    const idempotencyKey = `topup_${Date.now()}_${Math.random().toString(36).substring(7)}`

    try {
      await addSparksMutation.mutateAsync({ sparks, idempotencyKey })
      setAmount('')
    } catch (error) {
      console.error('Failed to add sparks:', error)
    }
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-surface-1 border border-white/10 rounded-lg" data-testid="add-sparks-section">
      {/* Balance display */}
      <div className="flex items-center gap-1.5 shrink-0">
        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
        </svg>
        <span className="text-sm font-medium text-foreground" data-testid="modal-sparks-balance">
          {sparksLoading ? '...' : (sparksData?.balance ?? 0).toLocaleString()}
        </span>
        <span className="text-xs text-foreground/50">sparks</span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Add sparks form */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount"
          min="1"
          className="w-24 px-2.5 py-1.5 bg-surface-light border border-white/10 rounded-md text-foreground placeholder-border text-xs focus:outline-none focus:border-border"
          data-testid="add-sparks-input"
        />
        <button
          onClick={handleAddSparks}
          disabled={!amount || parseInt(amount, 10) <= 0 || addSparksMutation.isPending}
          className="px-3 py-1.5 bg-primary/80 hover:bg-primary text-foreground text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          data-testid="add-sparks-button"
        >
          {addSparksMutation.isPending ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  )
}

function StatCard({ icon, value, label }: { icon: React.ReactNode; value: string | number; label: string }) {
  const display = typeof value === 'number' ? value.toLocaleString() : value
  return (
    <div className="flex-1 flex flex-col items-center gap-1 px-4 py-3 bg-surface-1 border border-white/10 rounded-md">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-2xl font-bold text-foreground">{display}</span>
      </div>
      <span className="text-xs text-foreground/50">{label}</span>
    </div>
  )
}

function NodeRow({ node }: { node: NodeUsageItem }) {
  const label = NODE_TYPE_LABELS[node.node_type] || node.node_type
  const hasImages = node.images_generated > 0
  const hasVideos = node.videos_generated > 0
  const hasTokens = node.input_tokens + node.output_tokens > 0
  const failed = node.status === 'FAILED'

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors ${failed ? 'opacity-45' : ''}`}>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm text-foreground truncate">{label}</span>
        {failed && (
          <span className="shrink-0 px-1.5 py-0.5 text-xxs font-medium bg-red-500/20 text-red-300 rounded">
            FAILED
          </span>
        )}
      </div>
      <div className="flex items-center gap-4 text-xs text-foreground/50 shrink-0">
        {/* Sparks */}
        <span className="flex items-center gap-1 w-14 justify-end" title={`${node.charged_sparks} sparks charged`}>
          <svg className="w-3.5 h-3.5 text-yellow-300/70" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          <span className="text-foreground/70 font-medium">{node.charged_sparks}</span>
        </span>

        {/* Tokens */}
        <span className="w-16 text-right" title={hasTokens ? `${node.input_tokens.toLocaleString()} in / ${node.output_tokens.toLocaleString()} out` : ''}>
          {hasTokens ? (
            <span className="flex items-center gap-1 justify-end">
              <svg className="w-3.5 h-3.5 text-emerald-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>{formatTokens(node.input_tokens + node.output_tokens)}</span>
            </span>
          ) : <span>—</span>}
        </span>

        {/* Media count */}
        <span className="w-14 text-right">
          {hasImages && (
            <span className="flex items-center gap-1 justify-end" title={`${node.images_generated} images`}>
              <svg className="w-3.5 h-3.5 text-blue-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
              <span>{node.images_generated}</span>
            </span>
          )}
          {hasVideos && (
            <span className="flex items-center gap-1 justify-end" title={`${node.videos_generated} videos`}>
              <svg className="w-3.5 h-3.5 text-purple-300/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>{node.videos_generated}</span>
            </span>
          )}
          {!hasImages && !hasVideos && <span>—</span>}
        </span>

        {/* Duration */}
        <span className="w-16 text-right tabular-nums">{formatDuration(node.duration_ms)}</span>
      </div>
    </div>
  )
}

export default function UsageCostModal({ isOpen, onClose, projectId }: UsageCostModalProps) {
  const { data, isLoading, error } = useCostSummary(isOpen ? projectId : undefined)

  const isEmpty = !isLoading && !error && (!data || data.node_count === 0)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sparks & Usage" size="md">
      {/* Add Sparks section — always visible at top */}
      <div className="mb-5">
        <AddSparksSection />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          <span className="ml-3 text-text-secondary">Loading usage data...</span>
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
          Failed to load usage data
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-12 h-12 text-foreground/15 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
            <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-text-muted text-sm">No usage data yet</p>
          <p className="text-foreground/25 text-xs mt-1">Run a workflow to see cost and usage details here.</p>
        </div>
      )}

      {data && data.node_count > 0 && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="flex gap-3">
            <StatCard
              icon={
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
                </svg>
              }
              value={data.total_sparks_charged}
              label="sparks"
            />
            <StatCard
              icon={
                <svg className="w-5 h-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              }
              value={data.total_images}
              label="images"
            />
            <StatCard
              icon={
                <svg className="w-5 h-5 text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              }
              value={data.total_videos}
              label="videos"
            />
            <StatCard
              icon={
                <svg className="w-5 h-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
              value={formatTokens(data.total_input_tokens + data.total_output_tokens)}
              label="tokens"
            />
          </div>

          {/* Node breakdown */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/30 uppercase tracking-wider">Node Breakdown</span>
              <span className="text-xs text-foreground/30">{data.node_count} nodes</span>
            </div>
            <div className="border border-white/[0.07] rounded-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center gap-3 px-3 py-2 bg-white/[0.03] border-b border-white/[0.07] text-xxs text-foreground/30 uppercase tracking-wider">
                <div className="flex-1">Node</div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="w-14 text-right">Sparks</span>
                  <span className="w-16 text-right">Tokens</span>
                  <span className="w-14 text-right">Media</span>
                  <span className="w-16 text-right">Duration</span>
                </div>
              </div>
              {/* Rows */}
              <div className="max-h-[280px] overflow-y-auto divide-y divide-white/[0.04]">
                {data.nodes.map((node, idx) => (
                  <NodeRow key={`${node.node_id}-${idx}`} node={node} />
                ))}
              </div>
            </div>
          </div>

          {/* Footer — estimated vs charged */}
          <div className="text-center text-xs text-foreground/30 pt-1">
            <div>Estimated: {data.total_sparks_estimated} sparks · Charged: {data.total_sparks_charged} sparks</div>
          </div>
        </div>
      )}
    </Modal>
  )
}
